import React, { useState, useEffect, useCallback } from "react";
import {
  User,
  Lock,
  Bell,
  Palette,
  Shield,
  Download,
  Trash2,
  Eye,
  EyeOff,
  Save,
  RefreshCw,
  Monitor,
  Moon,
  Sun,
  Camera,
  Check,
  X,
  AlertTriangle,
  Info,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { apiService } from "../services/api";
import "./Settings.css";

const Settings = () => {
  const { user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState("profile");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);

  // Profile settings
  const [profileData, setProfileData] = useState({
    name: "",
    email: "",
    bio: "",
    location: "",
    website: "",
    avatar: null,
  });

  // Security settings
  const [securityData, setSecurityData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    twoFactorEnabled: false,
  });

  // Notification settings
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    assessmentReminders: true,
    learningPathUpdates: true,
    weeklyProgress: true,
    marketingEmails: false,
    soundEnabled: true,
  });

  // Appearance settings
  const [appearanceSettings, setAppearanceSettings] = useState({
    theme: "light", // light, dark, system
    language: "en",
    timezone: "UTC",
    dateFormat: "MM/DD/YYYY",
    compactMode: false,
  });

  // Privacy settings
  const [privacySettings, setPrivacySettings] = useState({
    profileVisibility: "public", // public, private, friends
    showProgress: true,
    showAchievements: true,
    allowMessages: true,
    dataCollection: true,
  });

  const loadUserSettings = useCallback(async () => {
    try {
      setLoading(true);

      // Try to load from API, fallback to localStorage
      const savedSettings = localStorage.getItem("userSettings");
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        setNotificationSettings((prev) => ({
          ...prev,
          ...settings.notifications,
        }));
        setAppearanceSettings((prev) => ({ ...prev, ...settings.appearance }));
        setPrivacySettings((prev) => ({ ...prev, ...settings.privacy }));
      }

      // Apply theme
      applyTheme(appearanceSettings.theme);
    } catch (error) {
      console.error("Failed to load settings:", error);
    } finally {
      setLoading(false);
    }
  }, [appearanceSettings.theme]);

  // Load user data on component mount
  useEffect(() => {
    const loadUserProfile = async () => {
      if (user) {
        // First set from AuthContext user data
        setProfileData({
          name:
            user.name ||
            `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
            "",
          email: user.email || "",
          bio: user.bio || "",
          location: user.location || "",
          website: user.website || "",
          avatar: user.avatar || null,
        });

        // Then try to fetch full profile from API
        try {
          const profileResponse = await apiService.users.getProfile();
          if (profileResponse && !profileResponse.error) {
            const fullName =
              profileResponse.firstName && profileResponse.lastName
                ? `${profileResponse.firstName} ${profileResponse.lastName}`.trim()
                : profileResponse.full_name || user.name || "";

            setProfileData({
              name: fullName,
              email: profileResponse.email || user.email || "",
              bio: profileResponse.bio || "",
              location: profileResponse.location || "",
              website: profileResponse.website || "",
              avatar:
                profileResponse.avatarUrl || profileResponse.avatar_url || null,
            });
          }
        } catch (error) {
          console.error("Failed to load full profile:", error);
          // Keep the data from AuthContext
        }
      }
    };

    loadUserProfile();
    // Load settings from localStorage or API
    loadUserSettings();
  }, [user, loadUserSettings]);

  const applyTheme = (theme) => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark-theme");
    } else if (theme === "light") {
      root.classList.remove("dark-theme");
    } else {
      // System theme
      const prefersDark = window.matchMedia(
        "(prefers-color-scheme: dark)",
      ).matches;
      if (prefersDark) {
        root.classList.add("dark-theme");
      } else {
        root.classList.remove("dark-theme");
      }
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 5000);
  };

  const handleProfileSave = async () => {
    try {
      setSaving(true);

      // Validate required fields
      if (!profileData.name.trim()) {
        showMessage("error", "Name is required");
        return;
      }

      if (!profileData.email.trim()) {
        showMessage("error", "Email is required");
        return;
      }

      // Split name into firstName and lastName for API
      const nameParts = profileData.name.trim().split(" ");
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";

      // Update profile via API
      const result = await apiService.users.updateProfile({
        firstName,
        lastName,
        email: profileData.email,
        bio: profileData.bio,
        location: profileData.location,
        website: profileData.website,
        avatarUrl: profileData.avatar,
      });

      if (
        result?.success ||
        result?.message === "Profile updated successfully"
      ) {
        // Update the AuthContext with new user data
        updateUser({
          name: profileData.name,
          firstName,
          lastName,
          email: profileData.email,
          bio: profileData.bio,
          location: profileData.location,
          website: profileData.website,
          avatar: profileData.avatar,
        });
        showMessage("success", "Profile updated successfully");
      } else {
        showMessage(
          "error",
          result?.error || result?.message || "Failed to update profile",
        );
      }
    } catch (error) {
      console.error("Profile update error:", error);
      showMessage("error", error.message || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    try {
      setSaving(true);

      // Validate passwords
      if (!securityData.currentPassword) {
        showMessage("error", "Current password is required");
        return;
      }

      if (securityData.newPassword.length < 8) {
        showMessage("error", "New password must be at least 8 characters");
        return;
      }

      if (securityData.newPassword !== securityData.confirmPassword) {
        showMessage("error", "New passwords don't match");
        return;
      }

      // Change password via API
      const result = await apiService.auth.changePassword({
        currentPassword: securityData.currentPassword,
        newPassword: securityData.newPassword,
      });

      if (result?.success) {
        setSecurityData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
          twoFactorEnabled: securityData.twoFactorEnabled,
        });
        showMessage("success", "Password changed successfully");
      } else {
        showMessage("error", result?.message || "Failed to change password");
      }
    } catch (error) {
      console.error("Password change error:", error);
      showMessage("error", "Failed to change password");
    } finally {
      setSaving(false);
    }
  };

  const handleSettingsSave = async () => {
    try {
      setSaving(true);

      const settings = {
        notifications: notificationSettings,
        appearance: appearanceSettings,
        privacy: privacySettings,
      };

      // Save to localStorage
      localStorage.setItem("userSettings", JSON.stringify(settings));

      // Apply theme changes
      applyTheme(appearanceSettings.theme);

      // Try to save to API
      try {
        await apiService.users.updateSettings(settings);
      } catch (apiError) {
        console.warn("API settings save failed, using localStorage:", apiError);
      }

      showMessage("success", "Settings saved successfully");
    } catch (error) {
      console.error("Settings save error:", error);
      showMessage("error", "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        // 5MB limit
        showMessage("error", "Avatar file size must be less than 5MB");
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileData((prev) => ({
          ...prev,
          avatar: e.target.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleExportData = async () => {
    try {
      setLoading(true);
      showMessage("info", "Preparing your data export...");

      // Simulate data export
      setTimeout(() => {
        const dataUrl =
          "data:text/json;charset=utf-8," +
          encodeURIComponent(
            JSON.stringify(
              {
                profile: profileData,
                settings: {
                  notifications: notificationSettings,
                  appearance: appearanceSettings,
                  privacy: privacySettings,
                },
                exportDate: new Date().toISOString(),
              },
              null,
              2,
            ),
          );

        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = "proskillnest-data.json";
        link.click();

        showMessage("success", "Data exported successfully");
        setLoading(false);
      }, 2000);
    } catch (error) {
      console.error("Export error:", error);
      showMessage("error", "Failed to export data");
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (
      window.confirm(
        "Are you sure you want to delete your account? This action cannot be undone.",
      )
    ) {
      try {
        setLoading(true);
        showMessage("info", "Deleting account...");

        // Simulate account deletion
        setTimeout(() => {
          showMessage(
            "success",
            "Account deletion initiated. You will receive a confirmation email.",
          );
          setLoading(false);
        }, 2000);
      } catch (error) {
        console.error("Delete account error:", error);
        showMessage("error", "Failed to delete account");
        setLoading(false);
      }
    }
  };

  const tabs = [
    { id: "profile", label: "Profile", icon: User },
    { id: "security", label: "Security", icon: Shield },
    { id: "notifications", label: "Notifications", icon: Bell },
    { id: "appearance", label: "Appearance", icon: Palette },
    { id: "privacy", label: "Privacy", icon: Lock },
    { id: "data", label: "Data", icon: Download },
  ];

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1>Settings</h1>
        <p>Manage your account preferences and privacy settings</p>
      </div>

      {message.text && (
        <div className={`settings-message ${message.type}`}>
          {message.type === "success" && <Check size={20} />}
          {message.type === "error" && <X size={20} />}
          {message.type === "info" && <Info size={20} />}
          <span>{message.text}</span>
        </div>
      )}

      <div className="settings-container">
        {/* Sidebar Navigation */}
        <div className="settings-sidebar">
          <nav className="settings-nav">
            {tabs.map((tab) => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  className={`settings-nav-item ${
                    activeTab === tab.id ? "active" : ""
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <IconComponent size={18} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Main Content */}
        <div className="settings-content">
          {/* Profile Tab */}
          {activeTab === "profile" && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Profile Information</h2>
                <p>Update your personal information and profile details</p>
              </div>

              <div className="profile-avatar-section">
                <div className="avatar-container">
                  <div className="avatar-preview">
                    {profileData.avatar ? (
                      <img src={profileData.avatar} alt="Avatar" />
                    ) : (
                      <User size={48} />
                    )}
                  </div>
                  <div className="avatar-actions">
                    <label className="btn btn-secondary">
                      <Camera size={16} />
                      Change Avatar
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarChange}
                        style={{ display: "none" }}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    value={profileData.name}
                    onChange={(e) =>
                      setProfileData((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="form-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    value={profileData.email}
                    onChange={(e) =>
                      setProfileData((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                    placeholder="Enter your email"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Bio</label>
                  <textarea
                    value={profileData.bio}
                    onChange={(e) =>
                      setProfileData((prev) => ({
                        ...prev,
                        bio: e.target.value,
                      }))
                    }
                    placeholder="Tell us about yourself"
                    rows={3}
                  />
                </div>

                <div className="form-group">
                  <label>Location</label>
                  <input
                    type="text"
                    value={profileData.location}
                    onChange={(e) =>
                      setProfileData((prev) => ({
                        ...prev,
                        location: e.target.value,
                      }))
                    }
                    placeholder="City, Country"
                  />
                </div>

                <div className="form-group">
                  <label>Website</label>
                  <input
                    type="url"
                    value={profileData.website}
                    onChange={(e) =>
                      setProfileData((prev) => ({
                        ...prev,
                        website: e.target.value,
                      }))
                    }
                    placeholder="https://yourwebsite.com"
                  />
                </div>
              </div>

              <div className="section-actions">
                <button
                  className="btn btn-primary"
                  onClick={handleProfileSave}
                  disabled={saving}
                >
                  {saving ? (
                    <RefreshCw size={16} className="spinning" />
                  ) : (
                    <Save size={16} />
                  )}
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === "security" && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Security Settings</h2>
                <p>Manage your password and security preferences</p>
              </div>

              <div className="security-section">
                <h3>Change Password</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Current Password</label>
                    <div className="password-input">
                      <input
                        type={showCurrentPassword ? "text" : "password"}
                        value={securityData.currentPassword}
                        onChange={(e) =>
                          setSecurityData((prev) => ({
                            ...prev,
                            currentPassword: e.target.value,
                          }))
                        }
                        placeholder="Enter current password"
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() =>
                          setShowCurrentPassword(!showCurrentPassword)
                        }
                      >
                        {showCurrentPassword ? (
                          <EyeOff size={16} />
                        ) : (
                          <Eye size={16} />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>New Password</label>
                    <div className="password-input">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={securityData.newPassword}
                        onChange={(e) =>
                          setSecurityData((prev) => ({
                            ...prev,
                            newPassword: e.target.value,
                          }))
                        }
                        placeholder="Enter new password"
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff size={16} />
                        ) : (
                          <Eye size={16} />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Confirm New Password</label>
                    <input
                      type="password"
                      value={securityData.confirmPassword}
                      onChange={(e) =>
                        setSecurityData((prev) => ({
                          ...prev,
                          confirmPassword: e.target.value,
                        }))
                      }
                      placeholder="Confirm new password"
                    />
                  </div>
                </div>

                <button
                  className="btn btn-primary"
                  onClick={handlePasswordChange}
                  disabled={saving}
                >
                  {saving ? (
                    <RefreshCw size={16} className="spinning" />
                  ) : (
                    <Lock size={16} />
                  )}
                  {saving ? "Changing..." : "Change Password"}
                </button>
              </div>

              <div className="security-section">
                <h3>Two-Factor Authentication</h3>
                <div className="setting-item">
                  <div className="setting-info">
                    <div className="setting-label">Enable 2FA</div>
                    <div className="setting-description">
                      Add an extra layer of security to your account
                    </div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={securityData.twoFactorEnabled}
                      onChange={(e) =>
                        setSecurityData((prev) => ({
                          ...prev,
                          twoFactorEnabled: e.target.checked,
                        }))
                      }
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === "notifications" && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Notification Preferences</h2>
                <p>
                  Choose how you want to be notified about updates and
                  activities
                </p>
              </div>

              <div className="notifications-grid">
                <div className="notification-category">
                  <h3>General Notifications</h3>

                  <div className="setting-item">
                    <div className="setting-info">
                      <div className="setting-label">Email Notifications</div>
                      <div className="setting-description">
                        Receive notifications via email
                      </div>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={notificationSettings.emailNotifications}
                        onChange={(e) =>
                          setNotificationSettings((prev) => ({
                            ...prev,
                            emailNotifications: e.target.checked,
                          }))
                        }
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <div className="setting-label">Push Notifications</div>
                      <div className="setting-description">
                        Receive browser push notifications
                      </div>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={notificationSettings.pushNotifications}
                        onChange={(e) =>
                          setNotificationSettings((prev) => ({
                            ...prev,
                            pushNotifications: e.target.checked,
                          }))
                        }
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <div className="setting-label">Sound Effects</div>
                      <div className="setting-description">
                        Play sounds for notifications
                      </div>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={notificationSettings.soundEnabled}
                        onChange={(e) =>
                          setNotificationSettings((prev) => ({
                            ...prev,
                            soundEnabled: e.target.checked,
                          }))
                        }
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>

                <div className="notification-category">
                  <h3>Learning Notifications</h3>

                  <div className="setting-item">
                    <div className="setting-info">
                      <div className="setting-label">Assessment Reminders</div>
                      <div className="setting-description">
                        Reminders for pending assessments
                      </div>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={notificationSettings.assessmentReminders}
                        onChange={(e) =>
                          setNotificationSettings((prev) => ({
                            ...prev,
                            assessmentReminders: e.target.checked,
                          }))
                        }
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <div className="setting-label">Learning Path Updates</div>
                      <div className="setting-description">
                        New content in your learning paths
                      </div>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={notificationSettings.learningPathUpdates}
                        onChange={(e) =>
                          setNotificationSettings((prev) => ({
                            ...prev,
                            learningPathUpdates: e.target.checked,
                          }))
                        }
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <div className="setting-label">Weekly Progress</div>
                      <div className="setting-description">
                        Weekly progress summaries
                      </div>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={notificationSettings.weeklyProgress}
                        onChange={(e) =>
                          setNotificationSettings((prev) => ({
                            ...prev,
                            weeklyProgress: e.target.checked,
                          }))
                        }
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <div className="setting-label">Marketing Emails</div>
                      <div className="setting-description">
                        Product updates and promotions
                      </div>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={notificationSettings.marketingEmails}
                        onChange={(e) =>
                          setNotificationSettings((prev) => ({
                            ...prev,
                            marketingEmails: e.target.checked,
                          }))
                        }
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="section-actions">
                <button
                  className="btn btn-primary"
                  onClick={handleSettingsSave}
                  disabled={saving}
                >
                  {saving ? (
                    <RefreshCw size={16} className="spinning" />
                  ) : (
                    <Save size={16} />
                  )}
                  {saving ? "Saving..." : "Save Preferences"}
                </button>
              </div>
            </div>
          )}

          {/* Appearance Tab */}
          {activeTab === "appearance" && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Appearance Settings</h2>
                <p>Customize how the application looks and feels</p>
              </div>

              <div className="appearance-grid">
                <div className="appearance-section">
                  <h3>Theme</h3>
                  <div className="theme-options">
                    <div
                      className={`theme-option ${
                        appearanceSettings.theme === "light" ? "active" : ""
                      }`}
                      onClick={() =>
                        setAppearanceSettings((prev) => ({
                          ...prev,
                          theme: "light",
                        }))
                      }
                    >
                      <Sun size={24} />
                      <span>Light</span>
                    </div>
                    <div
                      className={`theme-option ${
                        appearanceSettings.theme === "dark" ? "active" : ""
                      }`}
                      onClick={() =>
                        setAppearanceSettings((prev) => ({
                          ...prev,
                          theme: "dark",
                        }))
                      }
                    >
                      <Moon size={24} />
                      <span>Dark</span>
                    </div>
                    <div
                      className={`theme-option ${
                        appearanceSettings.theme === "system" ? "active" : ""
                      }`}
                      onClick={() =>
                        setAppearanceSettings((prev) => ({
                          ...prev,
                          theme: "system",
                        }))
                      }
                    >
                      <Monitor size={24} />
                      <span>System</span>
                    </div>
                  </div>
                </div>

                <div className="appearance-section">
                  <h3>Language & Region</h3>
                  <div className="form-group">
                    <label>Language</label>
                    <select
                      value={appearanceSettings.language}
                      onChange={(e) =>
                        setAppearanceSettings((prev) => ({
                          ...prev,
                          language: e.target.value,
                        }))
                      }
                    >
                      <option value="en">English</option>
                      <option value="es">Español</option>
                      <option value="fr">Français</option>
                      <option value="de">Deutsch</option>
                      <option value="zh">中文</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Timezone</label>
                    <select
                      value={appearanceSettings.timezone}
                      onChange={(e) =>
                        setAppearanceSettings((prev) => ({
                          ...prev,
                          timezone: e.target.value,
                        }))
                      }
                    >
                      <option value="UTC">UTC</option>
                      <option value="America/New_York">Eastern Time</option>
                      <option value="America/Chicago">Central Time</option>
                      <option value="America/Denver">Mountain Time</option>
                      <option value="America/Los_Angeles">Pacific Time</option>
                      <option value="Europe/London">London</option>
                      <option value="Europe/Paris">Paris</option>
                      <option value="Asia/Tokyo">Tokyo</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Date Format</label>
                    <select
                      value={appearanceSettings.dateFormat}
                      onChange={(e) =>
                        setAppearanceSettings((prev) => ({
                          ...prev,
                          dateFormat: e.target.value,
                        }))
                      }
                    >
                      <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                      <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                      <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                    </select>
                  </div>
                </div>

                <div className="appearance-section">
                  <h3>Display Options</h3>
                  <div className="setting-item">
                    <div className="setting-info">
                      <div className="setting-label">Compact Mode</div>
                      <div className="setting-description">
                        Use smaller spacing and elements
                      </div>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={appearanceSettings.compactMode}
                        onChange={(e) =>
                          setAppearanceSettings((prev) => ({
                            ...prev,
                            compactMode: e.target.checked,
                          }))
                        }
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="section-actions">
                <button
                  className="btn btn-primary"
                  onClick={handleSettingsSave}
                  disabled={saving}
                >
                  {saving ? (
                    <RefreshCw size={16} className="spinning" />
                  ) : (
                    <Save size={16} />
                  )}
                  {saving ? "Saving..." : "Save Appearance"}
                </button>
              </div>
            </div>
          )}

          {/* Privacy Tab */}
          {activeTab === "privacy" && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Privacy Settings</h2>
                <p>Control your privacy and data sharing preferences</p>
              </div>

              <div className="privacy-sections">
                <div className="privacy-section">
                  <h3>Legal Agreements</h3>
                  <div className="legal-agreements">
                    <div className="agreement-item">
                      <div className="agreement-info">
                        <h4>Terms & Conditions</h4>
                        <p>
                          Last accepted:{" "}
                          {user?.termsAcceptedAt
                            ? new Date(
                                user.termsAcceptedAt,
                              ).toLocaleDateString()
                            : "Not available"}
                        </p>
                      </div>
                      <a
                        href="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                      >
                        View Terms
                      </a>
                    </div>

                    <div className="agreement-item">
                      <div className="agreement-info">
                        <h4>Privacy Policy</h4>
                        <p>
                          Last accepted:{" "}
                          {user?.privacyAcceptedAt
                            ? new Date(
                                user.privacyAcceptedAt,
                              ).toLocaleDateString()
                            : "Not available"}
                        </p>
                      </div>
                      <a
                        href="/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                      >
                        View Policy
                      </a>
                    </div>
                  </div>
                </div>

                <div className="privacy-section">
                  <h3>Profile Visibility</h3>
                  <div className="radio-group">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="profileVisibility"
                        value="public"
                        checked={privacySettings.profileVisibility === "public"}
                        onChange={(e) =>
                          setPrivacySettings((prev) => ({
                            ...prev,
                            profileVisibility: e.target.value,
                          }))
                        }
                      />
                      <span className="radio-label">
                        <strong>Public</strong>
                        <span>Anyone can see your profile</span>
                      </span>
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="profileVisibility"
                        value="private"
                        checked={
                          privacySettings.profileVisibility === "private"
                        }
                        onChange={(e) =>
                          setPrivacySettings((prev) => ({
                            ...prev,
                            profileVisibility: e.target.value,
                          }))
                        }
                      />
                      <span className="radio-label">
                        <strong>Private</strong>
                        <span>Only you can see your profile</span>
                      </span>
                    </label>
                  </div>
                </div>

                <div className="privacy-section">
                  <h3>Data Sharing</h3>

                  <div className="setting-item">
                    <div className="setting-info">
                      <div className="setting-label">Show Progress</div>
                      <div className="setting-description">
                        Allow others to see your learning progress
                      </div>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={privacySettings.showProgress}
                        onChange={(e) =>
                          setPrivacySettings((prev) => ({
                            ...prev,
                            showProgress: e.target.checked,
                          }))
                        }
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <div className="setting-label">Show Achievements</div>
                      <div className="setting-description">
                        Display your badges and achievements
                      </div>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={privacySettings.showAchievements}
                        onChange={(e) =>
                          setPrivacySettings((prev) => ({
                            ...prev,
                            showAchievements: e.target.checked,
                          }))
                        }
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <div className="setting-label">Allow Messages</div>
                      <div className="setting-description">
                        Let other users send you messages
                      </div>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={privacySettings.allowMessages}
                        onChange={(e) =>
                          setPrivacySettings((prev) => ({
                            ...prev,
                            allowMessages: e.target.checked,
                          }))
                        }
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <div className="setting-label">
                        Analytics & Improvement
                      </div>
                      <div className="setting-description">
                        Help improve the platform with usage data
                      </div>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={privacySettings.dataCollection}
                        onChange={(e) =>
                          setPrivacySettings((prev) => ({
                            ...prev,
                            dataCollection: e.target.checked,
                          }))
                        }
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="section-actions">
                <button
                  className="btn btn-primary"
                  onClick={handleSettingsSave}
                  disabled={saving}
                >
                  {saving ? (
                    <RefreshCw size={16} className="spinning" />
                  ) : (
                    <Save size={16} />
                  )}
                  {saving ? "Saving..." : "Save Privacy Settings"}
                </button>
              </div>
            </div>
          )}

          {/* Data Tab */}
          {activeTab === "data" && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Data Management</h2>
                <p>Export your data or delete your account</p>
              </div>

              <div className="data-sections">
                <div className="data-section">
                  <div className="data-action-card">
                    <div className="action-icon export">
                      <Download size={24} />
                    </div>
                    <div className="action-content">
                      <h3>Export Your Data</h3>
                      <p>
                        Download a copy of all your data including profile,
                        progress, and assessment results.
                      </p>
                      <button
                        className="btn btn-secondary"
                        onClick={handleExportData}
                        disabled={loading}
                      >
                        {loading ? (
                          <RefreshCw size={16} className="spinning" />
                        ) : (
                          <Download size={16} />
                        )}
                        {loading ? "Preparing..." : "Export Data"}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="data-section danger-section">
                  <div className="data-action-card danger">
                    <div className="action-icon delete">
                      <Trash2 size={24} />
                    </div>
                    <div className="action-content">
                      <h3>Delete Account</h3>
                      <p>
                        Permanently delete your account and all associated data.
                        This action cannot be undone.
                      </p>
                      <div className="danger-warning">
                        <AlertTriangle size={16} />
                        <span>This will permanently delete all your data</span>
                      </div>
                      <button
                        className="btn btn-danger"
                        onClick={handleDeleteAccount}
                        disabled={loading}
                      >
                        {loading ? (
                          <RefreshCw size={16} className="spinning" />
                        ) : (
                          <Trash2 size={16} />
                        )}
                        {loading ? "Deleting..." : "Delete Account"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
